import { Link } from "react-router-dom";
import { ArrowUpRight, Code2, Cloud, BrainCircuit, ShieldCheck, Boxes, Palette, Network, Workflow, Atom } from "lucide-react";
import { TECH_CATEGORIES, MARQUEE_TAGS } from "@/data/rapro";

const ICONS = {
  software: Code2,
  cloud: Cloud,
  ai: BrainCircuit,
  cybersec: ShieldCheck,
  blockchain: Boxes,
  uiux: Palette,
  network: Network,
  automation: Workflow,
  emerging: Atom,
};

export default function Technologies() {
  return (
    <div data-testid="technologies-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 dotted-grid opacity-40" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 md:pt-24 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-9 reveal">
              <div className="label-eyebrow text-slate-500" data-testid="tech-eyebrow">
                <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
                Technologies · High-End Staffing
              </div>
              <h1 data-testid="tech-headline" className="font-display mt-6 text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
                From <span className="text-[#00E676]">JavaScript</span> to <span className="underline decoration-[#00E676] decoration-8 underline-offset-4">Quantum</span> — and everything in between.
              </h1>
              <p className="mt-7 max-w-2xl text-slate-600 text-lg leading-relaxed">
                Nine technology practices. Hundreds of skills. One recruiting team that speaks every stack.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section data-testid="tech-marquee" className="border-y-2 border-slate-900 bg-[#00E676] text-slate-900 overflow-hidden">
        <div className="py-5 flex overflow-hidden whitespace-nowrap">
          <div className="marquee-track fast flex items-center gap-10 shrink-0 pr-10">
            {[...MARQUEE_TAGS, ...MARQUEE_TAGS].map((t, i) => (
              <span key={`tech-${t}-${i}`} className="font-display font-bold text-lg uppercase tracking-wider text-slate-900 flex items-center gap-10">
                {t}
                <span className="h-2 w-2 bg-slate-900 rounded-full" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24" data-testid="tech-categories-section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TECH_CATEGORIES.map((cat, idx) => {
            const Icon = ICONS[cat.id] || Code2;
            return (
              <article
                key={cat.id}
                data-testid={`tech-category-${cat.id}`}
                className="group bg-white border-2 border-slate-900 p-7 hard-shadow flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <div className="inline-flex h-12 w-12 items-center justify-center bg-[#00E676] group-hover:bg-[#00C853] transition-colors">
                    <Icon className="h-6 w-6 text-slate-900 group-hover:text-slate-900 transition-colors" />
                  </div>
                  <span className="font-display text-3xl font-extrabold text-slate-200">0{idx + 1}</span>
                </div>
                <h3 className="font-display text-xl font-bold mt-6 text-slate-900 leading-tight">{cat.title}</h3>
                <div className="mt-5 flex flex-wrap gap-2">
                  {cat.tags.map((tag) => (
                    <span
                      key={tag}
                      data-testid={`tech-tag-${cat.id}-${tag.replace(/\W+/g, "-").toLowerCase()}`}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium bg-slate-50 text-slate-900 border border-slate-200 hover:border-[#00E676] hover:bg-[#00E676]/10 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="tech-cta-section" className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Don't see a niche stack? We probably hire for it.
              </h2>
              <p className="mt-4 text-slate-600 text-lg max-w-2xl">
                Brief us on the role — we'll come back with a shortlist mapped to your seniority, location and budget.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/contact" data-testid="tech-final-cta" className="inline-flex items-center gap-2 bg-[#00E676] text-slate-900 px-6 py-3.5 font-semibold hover:bg-[#00C853] transition-colors">
                Start a Tech Search
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
